import z from 'zod';

// invoice template schema
const emailSchema = z.object({
  id: z.number(),
  tax_type: z.string(),
  cycle_type: z.string(),
  send_type: z.string(),
  created_at: z.string(),
  deleted_at: z.string().nullable(),
  subject: z.string(),
  content: z.string(),
});

const smsSchema = z.object({
  id: z.number(),
  tax_type: z.string(),
  cycle_type: z.string(),
  send_type: z.string(),
  created_at: z.string(),
  deleted_at: z.string().nullable(),
  subject: z.string(),
  content: z.string(),
});

export const emailSmsSchema = z.object({
  email: emailSchema,
  sms: smsSchema,
});

export const invoiceTemplateSchema = z
  .object({
    'regular-monthly': emailSmsSchema,
    'regular-weekly': emailSmsSchema,
    'reverse-monthly': emailSmsSchema,
    'reverse-weekly': emailSmsSchema,
  })
  .nullable();

// invoice template save schema
export const saveTemplateObjSchema = z.object({
  tax_type: z.string(),
  cycle_type: z.string(),
  send_type: z.string(),
  subject: z.string(),
  content: z.string(),
  deleted_at: z.string().nullable(),
});

export const saveTemplateArraySchema = z.array(saveTemplateObjSchema);

// 개별 템플릿 스키마 정의
const templateReplaceSchema = z.object({
  email: z.object({
    subject: z.string(),
    content: z.string(),
  }),
  sms: z.object({
    subject: z.string(),
    content: z.string(),
  }),
});

// 전체 스키마 정의
export const templateReplaceRecordSchema = z.object({ results: z.record(z.string(), templateReplaceSchema) });

//발송
export const SendDefaultTemplateData = z.object({
  id: z.number(),
  label: z.string().optional(),
  business_address: z.string(),
  business_name: z.string(),
  cs_contact: z.string(),
  email: z.string(),
  fax_contact: z.string(),
  file_name: z.string(),
  mail_content: z.string(),
  mail_subject: z.string(),
  manager_email: z.string(),
  manager_name: z.string(),
  team_contact: z.string(),
  team_name: z.string(),
  type: z.string().optional(),
  tax_invoice_method: z.string().optional(),
});

//발송
export const SendDefaultTemplateDataNew = z.object({
  id: z.number(),
  manager_name: z.string(),
  manager_email: z.string(),
  business_name: z.string(),
  business_address: z.string(),
  mail: z.object({
    subject: z.string(),
    content: z.string(),
  }),
  message: z.object({
    subject: z.string(),
    content: z.string(),
  }),
  file_name: z.string(),
  team_name: z.string(),
  team_contact: z.string(),
  cs_contact: z.string(),
  fax_contact: z.string(),
  email: z.string(),
  type: z.string().optional(),
  tax_invoice_method: z.string().optional(),
});

export const SettlementSendFilterData = z.object({
  publish_type: z.string(),
  settlement_period_type: z.string(),
  businessType: z.string(),
  send_period: z.string(),
  send_state: z.string(),
  send_type: z.string(),
  searchType: z.string(),
});

export const SendDefaultTemplateDataList = z.array(SendDefaultTemplateData);
export const SendDefaultTemplateDataNewList = z.array(SendDefaultTemplateDataNew);

export const SendListResponseListData = z.array(
  z.object({
    id: z.number(),
    gen_month: z.string(),
    report_available: z.boolean(),
    bill_available: z.boolean(),
    message_available: z.boolean(),
    business_type: z.string(),
    crn: z.string().nullable(),
    corp_name: z.string(),
    mail_receiver: z.array(z.string()).nullable(),
    message_receiver: z.array(z.string()).nullable(),
    agg_resources: z.array(z.string()),
    send_history: z.array(
      z.object({
        id: z.number(),
        date: z.string(),
        type: z.string(),
        contents: z.string(),
      }),
    ),
    publish_type: z.string(),
    settlement_period_type: z.string(),
    send_state: z.string(),
    send_type: z.string(),
    template_data: z.array(SendDefaultTemplateData),
  }),
);
export const SendListResponseData = z.object({
  id: z.number(),
  gen_month: z.string(),
  report_available: z.boolean(),
  bill_available: z.boolean(),
  message_available: z.boolean(),
  business_type: z.string(),
  crn: z.string().nullable(),
  corp_name: z.string(),
  mail_receiver: z.array(z.string()).nullable(),
  message_receiver: z.array(z.string()).nullable(),
  agg_resources: z.array(z.string()),
  send_history: z.array(
    z.object({
      id: z.number(),
      date: z.string(),
      type: z.string(),
      contents: z.string(),
    }),
  ),
  publish_type: z.string(),
  settlement_period_type: z.string(),
  send_state: z.string(),
  send_type: z.string(),
  template_data: z.array(SendDefaultTemplateData),
});

export const FormObjectSchema = z.object({
  type: z.string(),
  name: z.string(),
  options: z
    .array(
      z.object({
        value: z.string(),
        text: z.string(),
      }),
    )
    .nullable(),
  placeholder: z.string().optional(),
});

export const FormObjectSchemaArray = z.array(FormObjectSchema);

// 자원관리 정산 정보
// Location 스키마 정의
export const locationSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  location_code: z.string().nullable(),
  name: z.string(),
});

const contractSchema = z.object({
  id: z.number(),
  created_at: z.string().datetime(), // ISO 8601 datetime string
  deleted_at: z.string().nullable(), // nullable string
  haezoom_number: z.string(),
  contract_hz_date: z.string(), // ISO 8601 date string
  tax_invoice_method: z.string(),
  settlement_cycle: z.string(),
  contract_number: z.string().nullable(),
  contract_start_date: z.string(), // ISO 8601 date string
  contract_end_date: z.string(), // ISO 8601 date string
  auto_renewal: z.boolean(),
  e_powermarket_code: z.string(),
  generator_code: z.string(),
  main_company_ratio: z.string(), // Assuming this is a percentage in string format
  partner_company_ratio: z.string().nullable(), // nullable string
  delegation_settlement: z.boolean(),
  plant: z.number(),
  main_company: z.number(),
  partner_company: z.number().nullable(), // nullable number
});

// If you expect an array of contracts
const contractsArraySchema = z.array(contractSchema);
// Plant 스키마 정의
export const plantSchema = z.object({
  id: z.number().nullable(),
  location: locationSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  kpx_cbp_gen_id: z.string().nullable(),
  name: z.string(),
  plant_contracts: contractsArraySchema.optional(),
});

// MainCompany 스키마 정의
const mainCompanySchema = z
  .object({
    id: z.number(),
    created_at: z.string(),
    updated_at: z.string(),
    business_number: z.string(),
    company_name: z.string(),
    owner_name: z.string(),
    address: z.string(),
    business_type: z.string(),
    business_category: z.string(),
    bank_name: z.string(),
    account_name: z.string(),
    account_number: z.string(),
  })
  .nullable();

const baseSchema = z.object({
  id: z.number().nullable(),
  position: z.string(),
  name: z.string(),
  phone: z.string(),
  email: z.string(),
  memo: z.string().nullable(),
  allow_tax_invoice_sending: z.boolean(),
});

// baseSchema를 확장하여 동적 속성을 허용하는 스키마로 변경
export const memberInfoSchema = z
  .object({
    ...baseSchema.shape, // 기존 스키마의 정적 속성
  })
  .catchall(z.any()); // 추가적인 동적 속성을 허용

export const settlementType = z.object({
  key: z.string(),
  value: z.string(),
});

// SettlementContract 스키마 정의
export const settlementContractSchema = z
  .object({
    id: z.number(),
    plant: plantSchema,
    main_company: mainCompanySchema.nullable(),
    partner_company: mainCompanySchema.nullable(),
    member_infos: z.array(memberInfoSchema),
    created_at: z.string().nullable(),
    haezoom_number: z.string().nullable(),
    tax_invoice_method: z.string().nullable(),
    settlement_cycle: z.string().nullable(),
    contract_number: z.string().nullable(),
    contract_start_date: z.string().nullable(),
    contract_end_date: z.string().nullable(),
    contract_hz_date: z.string().nullable(),
    auto_renewal: z.boolean(),
    e_powermarket_code: z.string().nullable(),
    generator_code: z.string().nullable(),
    main_company_ratio: z.string().nullable(),
    partner_company_ratio: z.string().nullable(),
    delegation_settlement: z.boolean().nullable(),
  })
  .nullable();

// SettlementContract 스키마 정의
export const postSettlementContractSchema = z
  .object({
    id: z.number(),
    plant: plantSchema,
    main_company: mainCompanySchema.nullable(),
    partner_company: mainCompanySchema.nullable(),
    member_infos: z.array(memberInfoSchema),
    created_at: z.string().nullable(),
    haezoom_number: z.string().nullable(),
    tax_invoice_method: z.string().nullable(),
    settlement_cycle: z.string().nullable(),
    contract_number: z.string().nullable(),
    contract_start_date: z.string().nullable(),
    contract_end_date: z.string().nullable(),
    contract_hz_date: z.string().nullable(),
    auto_renewal: z.boolean(),
    e_powermarket_code: z.string().nullable(),
    generator_code: z.string().nullable(),
    main_company_ratio: z.string().nullable(),
    partner_company_ratio: z.string().nullable(),
    delegation_settlement: z.boolean().nullable(),
  })
  .nullable();

export const settlementContractArray = z.array(settlementContractSchema);
export const plantsLocationArray = z.array(plantSchema);
export const memberInfosArray = z.array(memberInfoSchema);
export const settlementTypeArray = z.array(settlementType);

//pdf
const PdfPlantSchema = z.object({
  kpx_cbp_gen_id: z.string(),
  name: z.string(),
  location_name: z.string(),
});

const ControlsSchema = z.object({
  id: z.number(),
  plant: plantSchema,
  started_at: z.string(),
  ended_at: z.string(),
  energy: z.number(),
  control: z.boolean(),
  target_energy: z.number(),
  metered_energy: z.number(),
  created_at: z.string(),
  deleted_at: z.string().nullable(),
  predicted_energy: z.number().nullable(),
  residual_energy: z.number().nullable(),
  command: z.number(),
});

const CompanyContractPaymentsSchema = z.object({
  haezoom_number: z.string(),
  business_number: z.string(),
  company_name: z.string(),
  company_type: z.string(),
  plant: PdfPlantSchema,
  date: z.string(), // ISO 형식의 날짜 문자열을 나타냅니다
  settlement_cycle: z.string(),
  payment: z.number(),
  metered_energy: z.number(),
  control_energy: z.number(),
  additional_payment: z.number(),
  energy_payment: z.number(),
  guarantee_control_payment: z.number(),
  controls: z.array(ControlsSchema),
  energy_trade_fee: z.number(),
});

const issue_info = z.object({
  invoice_issued_date: z.string(),
  invoice_registration_start_date: z.string(),
  invoice_registration_end_date: z.string(),
  invoice_start_date: z.string(),
  invoice_end_date: z.string(),
});

export const PdfInfoSchema = z.object({
  id: z.string(),
  business_number: z.string(),
  company_name: z.string(),
  company_type: z.string(),
  date: z.string(), // ISO 형식의 날짜 문자열을 나타냅니다
  settlement_cycle: z.string(),
  tax_invoice_method: z.string(),
  received: z.boolean(),
  payment: z.number(),
  metered_energy: z.number(),
  control_energy: z.number(),
  additional_payment: z.number(),
  energy_payment: z.number(),
  guarantee_control_payment: z.number(),
  company_contract_payments: z.array(CompanyContractPaymentsSchema),
  issue_info: issue_info,
  energy_trade_fee: z.number(),
});

//발송 관련

const messageSchema = z.object({
  sent_at: z.string(),
  message_sending_id: z.string(),
});

export const sentHistory = z.union([
  z.object({
    sms: z.array(messageSchema),
    email: z.array(messageSchema),
  }),
  z.object({}),
]);

export const companyPaymentSchema = z.object({
  id: z.string(), // UUID 형식의 문자열
  business_number: z.string(),
  company_name: z.string(),
  company_type: z.enum(['사업주', '협력사', '협력사(위탁)']),
  date: z.string(),
  settlement_cycle: z.enum(['월별', '1차수', '2차수', '3차수', '4차수']),
  tax_invoice_method: z.string(), // "M" 및 "P"가 있을 수 있음
  received: z.boolean(),
  payment: z.number(),
  metered_energy: z.number(),
  control_energy: z.number(),
  additional_payment: z.number(),
  energy_payment: z.number(),
  guarantee_control_payment: z.number(),
  phones: z.array(z.string()),
  emails: z.array(z.string()),
  issue_info: issue_info,
  template_data: z.array(SendDefaultTemplateData).optional(),
  invoice_template_data: templateReplaceSchema.optional(),
  sent_history: sentHistory,
});

export const companyPaymentSchemaArray = z.array(companyPaymentSchema);

// Email Info Schema
const emailInfoSchema = z.object({
  title: z.string(),
  body: z.string(),
});

// SMS Info Schema
const smsInfoSchema = z.object({
  body: z.string(),
});

// Main Schema
export const invoiceSchema = z.array(
  z.object({
    company_payment: companyPaymentSchema,
    send_type: z.array(z.string()),
    email_info: emailInfoSchema,
    sms_info: smsInfoSchema,
  }),
);

// default template를 가져오기위한 params Schema
export const getInvoiceTemplateParams = z.object({
  email: z.string(),
  issue_date: z.string(),
  kpx_year: z.string(),
  w_day: z.string(),
  w_month: z.string(),
  w_year: z.string(),
});
// Settlement Data Schema
export const SummarySettlementSchema = z.object({
  settlements: z.object({
    yesterday: z.object({
      revenue: z.number().default(0),
      payment: z.number().default(0),
      profit: z.number().default(0),
    }),
    this_month: z.object({
      revenue: z.number().default(0),
      payment: z.number().default(0),
      profit: z.number().default(0),
    }),
  }),
});

// Summary 고객 정보 데이터 스키마
export const SummaryCustomersSchema = z.object({
  current_customers: z.object({
    reverse_issued: z.number().default(0),
    regular_issued: z.number().default(0),
    cycle_settlement: z.number().default(0),
    monthly_settlement: z.number().default(0),
    fixed_contract: z.number().default(0),
    spot_customer: z.number().default(0),
  }),
});

// 두 스키마를 합친 새로운 스키마
export const SettlementDashboardSummarySchema = z.object({
  settlement: SummarySettlementSchema.shape.settlements,
  customerStatus: SummaryCustomersSchema.shape.current_customers,
});

// 월별/일별 정산 데이터 스키마
const SettlementDataSchema = z.object({
  revenue: z.number(),
  payment: z.number(),
  profit: z.number(),
  imbalance_penalty: z.number(),
});

// 차트 데이터 스키마
export const ChartDataSchema = z.object({
  monthly_settlements: z.record(z.string(), SettlementDataSchema).optional(),
  daily_settlements: z.record(z.string(), SettlementDataSchema).optional(),
});

// 정산 월 정보 api
export const SettlementDatesSchema = z.object({
  year: z.string().regex(/^\d{4}$/, 'Invalid year format'), // Year must be a 4-digit string
  month: z.string().regex(/^(0[1-9]|1[0-2])$/, 'Invalid month format'), // Month must be a 2-digit string (01-12)
  initial: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'), // Format: YYYY-MM-DD
  realTime: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'), // Format: YYYY-MM-DD
  final: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'), // Format: YYYY-MM-DD
});

// TypeScript type for the API response
export type DatesApiResponse = z.infer<typeof SettlementDatesSchema>;

// 월별 정산 상세 정보 스키마
export const MonthlySettlementDetailSchema = z.object({
  settlement_date: z.object({
    realtime: z.string().nullable(),
    init: z.string().nullable(),
    final: z.string().nullable(),
  }),
  summary: z.object({
    revenue: z.number().default(0),
    payment: z.number().default(0),
    profit: z.number().default(0),
    profit_rate: z.number().default(0),
    generation: z.number().default(0),
    capacity: z.number().default(0),
  }),
  revenue_detail: z.object({
    energy_settlement: z.number().default(0),
    variable_cost_settlement: z.number().default(0),
    capacity_settlement: z.number().default(0),
    forecast_settlement: z.number().default(0),
    imbalance_penalty: z.number().default(0),
    additional_settlement: z.number().default(0),
  }),
  payment_detail: z.object({
    electricity_settlement: z.number().default(0),
    haezoom_payment: z.number().default(0),
    additional_payment: z.number().default(0),
    electricity_fee: z.number().default(0),
  }),
  unit_price: z.object({
    energy_settlement_unit: z.number().default(0),
    variable_cost_unit: z.number().default(0),
    expected_profit_unit: z.number().default(0),
    capacity_settlement_unit: z.number().default(0),
    imbalance_penalty_unit: z.number().default(0),
    electricity_settlement_unit: z.number().default(0),
    haezoom_payment_unit: z.number().default(0),
    additional_payment_unit: z.number().default(0),
  }),
});

// 정산 대시보드 헤더정보
export const SourceDateSchema = z.object({
  init: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'), // YYYY-MM-DD 형식
  final: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'), // YYYY-MM-DD 형식
  billing_date: z
    .array(
      z.object({
        id: z.number(),
        created_at: z.string(),
        updated_at: z.string(),
        year: z.number(),
        month: z.number(),
        settlement_cycle: z.string(),
        invoice_issued_date: z.string(),
        invoice_registration_start_date: z.string(),
        invoice_registration_end_date: z.string(),
        invoice_start_date: z.string(),
        invoice_end_date: z.string(),
        payment_date: z.string(),
      }),
    )
    .default([]), // 빈 배열을 기본값으로 설정
  payment_date: z
    .array(
      z.object({
        id: z.number(),
        created_at: z.string(),
        updated_at: z.string(),
        year: z.number(),
        month: z.number(),
        settlement_cycle: z.string(),
        invoice_issued_date: z.string(),
        invoice_registration_start_date: z.string(),
        invoice_registration_end_date: z.string(),
        invoice_start_date: z.string(),
        invoice_end_date: z.string(),
        payment_date: z.string(),
      }),
    )
    .default([]), // 빈 배열을 기본값으로 설정
});

export const SettlementHeaderSchema = z.object({
  resource_groups: z.array(z.string()),
  source_date: SourceDateSchema,
});

const EventSchema = z.object({
  type: z.enum(['high', 'mid']),
  content: z.string(),
});

// 날짜별 배열 스키마
const DaySchema = z.array(EventSchema);

// 월 스키마
const MonthSchema = z.record(z.string(), DaySchema);

// 캘린더 스키마
export const CalendarSchema = z.object({
  calendar: z.record(z.string(), MonthSchema),
});
